import { apiGet, apiPost } from './client.js';
import type {
  CashCountMethod,
  CashDenominationConfig,
  DenominationCounts,
  VarianceLevel,
} from '@fixflow/shared-types';

export interface CashSummary {
  openingFloat: string;
  cashSales: string;
  cashRefunds: string;
  cashIn: string;
  cashOut: string;
  runningTotal: string;
  expectedCash: string;
}

export interface CashEntry {
  id: string;
  sessionId: string;
  type: string;
  amount: string;
  reason?: string | null;
  ticketId?: string | null;
  ticketNumber?: string | null;
  paymentId?: string | null;
  createdAt: string;
}

export interface StoreCashSettings {
  currency: string;
  cashDenominations: CashDenominationConfig;
  trackCoinRolls: boolean;
  cashVarianceTolerance: string;
  cashVarianceRequiresOverride: boolean;
  changeAlertsEnabled: boolean;
  changeMinimums: Record<string, number>;
  largeBillThreshold: string;
  dropThreshold: string;
  changeAlertsSmsOnCritical: boolean;
  changeAlertDedupeHours: number;
  dropAlertMode: 'never' | 'on_close' | 'realtime';
}

export interface CashSession {
  id: string;
  status: 'open' | 'closed';
  openingFloat: string;
  closingCount?: string | null;
  expectedCash?: string | null;
  variance?: string | null;
  notes?: string | null;
  closingNotes?: string | null;
  openingDenominations?: DenominationCounts;
  closingDenominations?: DenominationCounts;
  openingCountMethod?: CashCountMethod | null;
  closingCountMethod?: CashCountMethod | null;
  openedByName?: string | null;
  closedByName?: string | null;
  varianceOverrideByName?: string | null;
  openedAt: string;
  closedAt?: string | null;
  runningTotal: string;
  summary: CashSummary;
  zReport?: CashZReport | null;
  entries?: CashEntry[];
  totalCashSales?: string;
  storeCash?: StoreCashSettings | null;
  overTolerance?: boolean;
  varianceLevel?: VarianceLevel | null;
  shortageHint?: {
    key: string;
    label: string;
    valueCents: number;
  } | null;
}

export interface CashZReport {
  storeName: string;
  cashSalesCount: number;
  cashRefundsCount: number;
  cashInCount: number;
  cashOutCount: number;
  paymentBreakdown: {
    cash: string;
    card: string;
    storeCredit: string;
    other: string;
    totalRevenue: string;
    totalTickets: number;
  };
  openingDenominationLines?: Array<{ label: string; qty: number; lineCents: number }>;
  closingDenominationLines?: Array<{ label: string; qty: number; lineCents: number }>;
  openingCountMethod?: CashCountMethod | null;
  closingCountMethod?: CashCountMethod | null;
}

export interface CashSessionListParams {
  page?: number;
  pageSize?: number;
  status?: 'open' | 'closed';
}

export type OpenCashSessionBody = {
  countMethod?: CashCountMethod;
  openingFloat?: number;
  openingDenominations?: DenominationCounts;
  notes?: string;
  notifyManagers?: boolean;
};

export type CloseCashSessionBody = {
  countMethod?: CashCountMethod;
  closingCount?: number;
  closingDenominations?: DenominationCounts;
  notes?: string;
  closingNotes?: string;
  managerOverrideAuthorizedBy?: string;
  notifyManagers?: boolean;
};

export const cashSessionsApi = {
  getCurrent: () =>
    apiGet<CashSession | null>('/api/v1/cash-sessions/current').then((res) => res.data),
  getDenominationSettings: () =>
    apiGet<StoreCashSettings>('/api/v1/cash-sessions/denomination-settings').then(
      (res) => res.data,
    ),
  getChangeUsage: (sessions = 30) =>
    apiGet<{
      sessionsAnalyzed: number;
      suggestions: Array<{
        denomination: string;
        label: string;
        averageConsumedPerSession: number;
        sessionsWithUsage: number;
        suggestedMinimum: number;
        currentMinimum: number;
      }>;
      note: string;
    }>('/api/v1/cash-sessions/change-usage', { params: { sessions } }).then((res) => res.data),
  open: (body: OpenCashSessionBody) =>
    apiPost<CashSession, OpenCashSessionBody>('/api/v1/cash-sessions/open', body).then(
      (res) => res.data,
    ),
  cashIn: (id: string, body: { amount: number; reason: string }) =>
    apiPost<CashSession, typeof body>(`/api/v1/cash-sessions/${id}/cash-in`, body).then(
      (res) => res.data,
    ),
  cashOut: (id: string, body: { amount: number; reason: string }) =>
    apiPost<CashSession, typeof body>(`/api/v1/cash-sessions/${id}/cash-out`, body).then(
      (res) => res.data,
    ),
  close: (id: string, body: CloseCashSessionBody) =>
    apiPost<CashSession, CloseCashSessionBody>(`/api/v1/cash-sessions/${id}/close`, body).then(
      (res) => res.data,
    ),
  getById: (id: string) =>
    apiGet<CashSession>(`/api/v1/cash-sessions/${id}`).then((res) => res.data),
  list: (params?: CashSessionListParams) =>
    apiGet<
      CashSession[],
      { page: number; pageSize: number; total: number; storeCash?: StoreCashSettings }
    >('/api/v1/cash-sessions', {
      params,
    }),
};

export function getCurrentCashSession() {
  return cashSessionsApi.getCurrent();
}

export function openCashSession(body: OpenCashSessionBody) {
  return cashSessionsApi.open(body);
}

export function addCashIn(id: string, body: { amount: number; reason: string }) {
  return cashSessionsApi.cashIn(id, body);
}

export function addCashOut(id: string, body: { amount: number; reason: string }) {
  return cashSessionsApi.cashOut(id, body);
}

export function closeCashSession(id: string, body: CloseCashSessionBody) {
  return cashSessionsApi.close(id, body);
}

export function listCashSessions(params?: CashSessionListParams) {
  return cashSessionsApi.list(params);
}

export function getCashSession(id: string) {
  return cashSessionsApi.getById(id);
}
